import { Property, type PropertyList } from '@kalos/kalos-rpc';
import {
  Button,
  DataTable,
  DataTableColumnHeader,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kalos/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Bell, Pencil, X } from 'lucide-react';
import { useMemo, useState } from 'react';

import { PropertyClientService } from '../../../tools/helpers';
import { NotificationDialog } from './NotificationDialog';
import { getNotificationImportance, IMPORTANCE_TEXT_CLASSES } from './notificationImportance';

interface PropertyWithNotification extends Property {
  notificationLength: number;
}

type PropertyNotificationsDialogProps = {
  customerId: number;
  trigger?: React.ReactNode;
};

interface Filters {
  address: string;
  importance: string;
  notification: string;
}

export const PropertyNotificationsDialog: React.FC<PropertyNotificationsDialogProps> = ({
  customerId,
  trigger,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState<PropertyWithNotification | null>(null);
  const [filters, setFilters] = useState<Filters>({
    address: '',
    importance: '',
    notification: '',
  });
  const queryClient = useQueryClient();

  // Only fetch data when dialog is open
  const propertiesQuery = useQuery({
    queryKey: ['property-notifications', customerId],
    queryFn: async (): Promise<PropertyList> => {
      const req = Property.create({
        userId: customerId,
        withoutLimit: true,
        isActive: 1,
      });

      const response = await PropertyClientService.BatchGet(req);
      return response;
    },
    enabled: isOpen && !!customerId,
  });

  // Filter and sort properties by notification importance (length)
  const importantProperties = useMemo(() => {
    if (!propertiesQuery.data?.results) return [];

    return propertiesQuery.data.results
      .filter((property) => property.notification && property.notification.trim().length > 0)
      .map(
        (property): PropertyWithNotification => ({
          ...property,
          notificationLength: property.notification.length,
        }),
      )
      .sort((a, b) => b.notificationLength - a.notificationLength);
  }, [propertiesQuery.data?.results]);

  // Apply filters to the properties
  const filteredProperties = useMemo(() => {
    return importantProperties.filter((property) => {
      const fullAddress = [property.address, property.city, property.state, property.zip]
        .filter(Boolean)
        .join(', ')
        .toLowerCase();
      const addressMatch =
        filters.address === '' || fullAddress.includes(filters.address.toLowerCase());

      const importance = getNotificationImportance(property.notificationLength);
      const importanceMatch =
        filters.importance === '' ||
        filters.importance === 'all' ||
        importance === filters.importance;

      const notificationMatch =
        filters.notification === '' ||
        property.notification.toLowerCase().includes(filters.notification.toLowerCase());

      return addressMatch && importanceMatch && notificationMatch;
    });
  }, [importantProperties, filters]);

  const handleFilterChange = (field: keyof Filters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      address: '',
      importance: '',
      notification: '',
    });
  };

  const hasActiveFilters = Object.values(filters).some((filter) => filter !== '');

  const getPropertyAddress = (property: PropertyWithNotification) => {
    return [property.address, property.city, property.state, property.zip]
      .filter(Boolean)
      .join(', ');
  };

  const handleSaveNotification = async (notification: string) => {
    if (!editingProperty) return;
    await PropertyClientService.Update(
      Property.create({
        id: editingProperty.id,
        notification,
        fieldMask: ['Notification'],
      }),
    );
    await queryClient.invalidateQueries({ queryKey: ['property-notifications', customerId] });
    setEditingProperty(null);
  };

  const columns = useMemo<ColumnDef<PropertyWithNotification>[]>(() => {
    return [
      {
        accessorKey: 'address',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Property Address" />,
        meta: {
          className: 'w-64 align-top',
        },
        cell: ({ row, column }) => {
          const property = row.original;
          const fullAddress = getPropertyAddress(property);

          return <div className={column.columnDef.meta?.className}>{fullAddress}</div>;
        },
      },
      {
        accessorKey: 'notificationLength',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Importance" />,
        meta: {
          className: 'w-24 text-center align-top',
        },
        cell: ({ row, column }) => {
          const importance = getNotificationImportance(row.original.notificationLength);

          return (
            <div
              className={`${column.columnDef.meta?.className} ${IMPORTANCE_TEXT_CLASSES[importance]}`}
            >
              {importance}
            </div>
          );
        },
      },
      {
        accessorKey: 'notification',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Notification" />,
        meta: {
          className: 'align-top',
        },
        cell: ({ row, column }) => (
          <div
            className={`${column.columnDef.meta?.className} whitespace-pre-wrap break-words text-sm`}
          >
            {row.original.notification}
          </div>
        ),
      },
      {
        id: 'actions',
        header: () => null,
        meta: {
          className: 'w-12 align-top',
        },
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setEditingProperty(row.original)}
          >
            <Pencil className="h-4 w-4" />
            <span className="sr-only">Edit notification</span>
          </Button>
        ),
      },
    ];
  }, []);

  const table = useReactTable({
    data: filteredProperties,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      sorting: [{ id: 'notificationLength', desc: true }],
    },
  });

  const defaultTrigger = (
    <Button size="sm" className="flex items-center gap-1" disabled={!customerId}>
      <Bell size={15} />
      Property Notifications
    </Button>
  );

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{trigger || defaultTrigger}</DialogTrigger>
      <DialogContent className="flex max-h-[85vh] w-[95vw] max-w-6xl flex-col overflow-hidden bg-white">
        <DialogTitle>Important Property Notifications</DialogTitle>

        <div className="flex-1 overflow-auto">
          {propertiesQuery.isPending ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-gray-500">Loading property notifications...</div>
            </div>
          ) : propertiesQuery.isError ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-red-500">Error loading property notifications</div>
            </div>
          ) : importantProperties.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-gray-500">No important property notifications found</div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing {filteredProperties.length} of {importantProperties.length} properties
                  with notifications
                </div>
                {hasActiveFilters && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearFilters}
                    className="flex items-center gap-1"
                  >
                    <X size={14} />
                    Clear Filters
                  </Button>
                )}
              </div>

              {/* Filter Controls */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Filter by Address</label>
                  <Input
                    placeholder="Enter address..."
                    value={filters.address}
                    onChange={(e) => handleFilterChange('address', e.target.value)}
                    className="w-full"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Filter by Importance</label>
                  <Select
                    value={filters.importance}
                    onValueChange={(value) => handleFilterChange('importance', value)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="All importance levels" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All importance levels</SelectItem>
                      <SelectItem value="High">High</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="Low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Filter by Notification Content</label>
                  <Input
                    placeholder="Search notification content..."
                    value={filters.notification}
                    onChange={(e) => handleFilterChange('notification', e.target.value)}
                    className="w-full"
                  />
                </div>
              </div>

              <DataTable table={table} headerClassName="bg-white" />
            </div>
          )}
        </div>

        <NotificationDialog
          title="Property Notification"
          open={!!editingProperty}
          onOpenChange={(open) => {
            if (!open) setEditingProperty(null);
          }}
          defaultValue={editingProperty?.notification ?? ''}
          onSave={async ({ data: { notification } }) => {
            await handleSaveNotification(notification);
          }}
          onDelete={editingProperty?.notification ? () => handleSaveNotification('') : undefined}
        />
      </DialogContent>
    </Dialog>
  );
};
